import React from "react";
import { Badge } from "@material-ui/core";
import { BRAND_GOLD, BRAND_INK } from "../config/brandColors";
import { Menu, ShoppingCartOutlined } from "@material-ui/icons";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { Image } from "react-bootstrap";
import SearchIcon from "../assets/img/search_icon.png";
import { mobile } from "../responsive";
import LocationPrompt from "./LocationPrompt";

/* The cart count in the nav.

   It was MUI's `color="secondary"`, which paints a red pill on this bar's
   #324d54 and measures 2.16:1 — under the 3:1 a graphic needs to be legible at
   all, so on a phone in daylight the number was a smudge. Red is also not a
   colour this app owns; it arrived by theme default.

   Gold is the brand accent AND the far better read here: 5.28:1 on the bar,
   with the numeral at 9.36:1 on the pill. The glyph beside it stays near-white
   deliberately — the nav icons are a set, and pulling only the cart into gold
   would read as a selected state rather than as branding. */
const NavCartBadge = styled(Badge)`
  .MuiBadge-badge {
    background-color: ${BRAND_GOLD};
    color: ${BRAND_INK};
    font-weight: 800;
  }
`;

const Container = styled.div`
  position: fixed;
  top: 0;
  width: 100vw; /* Ensure it covers the full viewport width */
  z-index: 1000;
  background-color: #324d54;
  left: 0; /* Ensure it starts from the very left of the viewport */
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #324d54;
  position: relative;
  box-sizing: border-box;

  @media only screen and (max-width: 850px) {
    flex-direction: column;
    align-items: stretch;
    padding: 8px 12px 0 12px;
    gap: 8px;
  }
`;

const Logo = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
`;

const Left = styled.div`
  flex: 1;
  display: flex;
  align-items: center;

  @media only screen and (max-width: 850px) {
    display: none;
  }
`;

const Right = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 40px;

  @media only screen and (max-width: 850px) {
    display: none;
  }
`;

const LocationChip = styled.div`
  display: flex;
  align-items: center;
  margin-right: 14px;
  max-width: 260px;
`;

const MenuItem = styled.div`
  font-size: 14px;
  cursor: pointer;
  margin-left: 25px;

  @media only screen and (max-width: 850px) {
    margin-left: 0;
  }
`;

const Center = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  @media only screen and (max-width: 850px) {
    display: none;
  }
`;

const MobileTopRow = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    align-items: center;
    width: 100%;
  }
`;

const MobileBottomRow = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    padding-bottom: 8px;
    gap: 6px;
  }
`;

const MobileRight = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    flex-shrink: 0;
  }
`;

const MobileLocationBar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const MobileSearchContainer = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    align-items: stretch;
    border: 1px solid lightgray;
    border-radius: 10px;
    width: 100%;
    min-width: 0;
    overflow: hidden;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid lightgray;
  border-radius: 10px;
  flex: 2;
  padding: 0;
  margin-right: 25px;
  width: 100%;
`;

const Input = styled.input`
  flex-grow: 1;
  min-width: 0;
  padding: 10px;
  padding-left: 32px;
  font-size: 14px;
  border: none;
  border-radius: 10px 0 0 10px;
  box-sizing: border-box;
  background: white url(${SearchIcon}) no-repeat 10px center;
  background-repeat: no-repeat;
  background-position: 10px center;
  background-size: 17px;
  margin: 0;
  &:focus {
    outline: none;
  }
  ${mobile({ flexGrow: 1 })}
`;

const SearchButton = styled.button`
  padding: 10px 15px;
  font-size: 12px;
  border: none;
  background-color: #83b524;
  color: white;
  cursor: pointer;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  box-shadow: -6px 2px 10px rgba(0, 0, 0, 0.55);
  &:focus {
    outline: none;
    background-color: #699b07;
  }
  &:hover {
    background-color: #699b07;
  }
`;

const ClickableMenu = styled(Menu)`
  font-size: 34px;
  color: white;
  margin-left: 20px;
  opacity: 0.8;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #ff6347; // Tomato color when mouse hovers over it
  }

  &:active {
    transform: scale(0.9);
  }

  &:focus {
    outline: 2px solid #ff6347; // Outline with tomato color when it gets focus
  }
`;

const RestaurantNavbar = ({ setIsCartVisible }) => {
  const history = useHistory();
  const currentRestaurant = useSelector(
    (state) => state.restaurantCart.currentRestaurant
  );
  const cart = useSelector(
    (state) => state.restaurantCart.carts[currentRestaurant] || {}
  );
  const cartCount = Object.values(cart).reduce(
    (total, item) => total + item.quantity,
    0
  );

  const handleCartClick = () => {
    if (setIsCartVisible) {
      setIsCartVisible(true);
    } else {
      history.push(`/restaurant/shop/${currentRestaurant}`);
    }
  };

  const goToProfile = () => {
    history.push("/my_profile"); // Change this to your desired route
  };

  return (
    <Container>
      <Wrapper>
        {/* ── Desktop layout ── */}
        <Left>
          <Logo>
            <Link to="/">
              {/* Match the wordmark logo used by the main Navbar
                  (lusuku_logo_main.png) instead of the bare PWA icon. */}
              <Image
                src="/lusuku_logo_main.png"
                alt="Lusuku"
                style={{
                  width: 95,
                  height: 95,
                  marginBottom: 10,
                  marginRight: 8,
                  imageRendering: "-webkit-optimize-contrast",
                }}
              />
            </Link>
          </Logo>
        </Left>
        <Center>
          <SearchContainer>
            <Input type="search" id="search" placeholder="Search products, brands and categories..." />
            <SearchButton>SEARCH</SearchButton>
          </SearchContainer>
        </Center>
        <Right>
          {/* Set delivery location — shows what the customer set and lets them
              change it, so they aren't guessing why a vendor is out of range. */}
          <LocationChip>
            <LocationPrompt compact />
          </LocationChip>
          <MenuItem onClick={handleCartClick}>
            <NavCartBadge overlap="rectangular" badgeContent={cartCount}>
              <ShoppingCartOutlined style={{ color: "#f8f4f4", fontSize: 34, borderRadius: "50%" }} />
            </NavCartBadge>
          </MenuItem>
          <ClickableMenu style={{ fontSize: 34, marginRight: 20 }} onClick={goToProfile} />
        </Right>

        {/* ── Mobile layout ── */}
        <MobileTopRow>
          <Logo>
            <Link to="/">
              <Image
                src="/lusuku_logo_main.png"
                alt="Lusuku"
                style={{ width: 52, height: 52, marginRight: 8, imageRendering: "-webkit-optimize-contrast" }}
              />
            </Link>
          </Logo>
          <MobileRight>
            <MenuItem onClick={handleCartClick}>
              <NavCartBadge overlap="rectangular" badgeContent={cartCount}>
                <ShoppingCartOutlined style={{ color: "#f8f4f4", fontSize: 22 }} />
              </NavCartBadge>
            </MenuItem>
            <ClickableMenu style={{ fontSize: 28, marginRight: 0 }} onClick={goToProfile} />
          </MobileRight>
        </MobileTopRow>
        <MobileBottomRow>
          <MobileLocationBar>
            <LocationPrompt compact />
          </MobileLocationBar>
          <MobileSearchContainer>
            <Input type="search" id="search-mobile" placeholder="Search products, brands and categories..." />
            <SearchButton>SEARCH</SearchButton>
          </MobileSearchContainer>
        </MobileBottomRow>
      </Wrapper>
    </Container>
  );
};

export default RestaurantNavbar;
